import type { FunctionComponent, HTMLProps } from 'react'
import React from 'react'

import classnames from 'classnames'

import type { ImageSource, ValidTypographyTags } from '../../types'
import { getInitials } from '../../utilities/parsers/getInitials'
import { AnimationWrapper } from '../AnimationWrapper'
import { BaseLink } from '../Links'
import { Picture } from '../Picture'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './Author.module.scss'

export const Author: FunctionComponent<Props> = ({
  centered,
  className,
  href,
  jobTitle,
  name,
  isHorizontal,
  sources,
  src,
  textOnly,
  authorNameTag = 'span',
  ...rest
}) => {
  const authorCard = (
    <div
      className={classnames(
        styles.author,
        centered && styles.authorCentered,
        className
      )}
      data-testid="author-card"
      {...rest}
    >
      {!textOnly &&
        (src ? (
          <div
            className={classnames(
              isHorizontal ? styles.authorHorizontal : styles.authorRoundel
            )}
            data-testid="author-roundel-image"
          >
            <Picture
              aspectRatioMain="one-to-one"
              className={classnames(
                isHorizontal ? styles.authorHorizontal : styles.authorRoundel
              )}
              src={src}
              sources={sources}
            />
          </div>
        ) : (
          <TypographyV2
            tag="span"
            textStyle="sb-text-interface-body-small-regular"
            className={styles.authorRoundelText}
            data-testid="author-roundel-text"
            aria-hidden="true"
          >
            {getInitials(name)}
          </TypographyV2>
        ))}
      <div className={styles.authorWrapper}>
        <TypographyV2
          data-testid="author-name"
          tag={authorNameTag}
          textStyle="sb-text-interface-body-small-regular"
          className={href && styles.authorName}
        >
          {href ? <AnimationWrapper>{name}</AnimationWrapper> : name}
        </TypographyV2>
        {jobTitle && (
          <TypographyV2
            className={classnames(href && styles.authorJobTitle)}
            data-testid="author-job-title"
            tag="span"
            textStyle="sb-text-interface-body-small-regular"
          >
            {jobTitle}
          </TypographyV2>
        )}
      </div>
    </div>
  )

  return href ? (
    <BaseLink href={href} data-testid="author-link">
      {authorCard}
    </BaseLink>
  ) : (
    authorCard
  )
}

///////// IMPLEMENTATION /////////

type Props = {
  /**
   *  Link to the author's page if required.
   */
  href?: string
  /**
   *  Name of the Author.
   */
  name: string
  /**
   *  Job title of the Author.
   */
  jobTitle?: string
  /**
   * Author's image to be used inside the roundel. NB This image must be in jpeg or gif format.
   */
  src?: string
  /**
   * Array of image sources.
   */
  sources?: ImageSource[]
  /**
   * Boolean value to determine whether roundel will be displayed or only display text within the component.
   */
  textOnly?: boolean
  /**
   * Optional boolean value to apply centered styling or not.
   */
  centered?: boolean
  /**
   * Class name that will be applied to the Author component for additional styling.
   */
  className?: string
  /**
   * Optional orientation style prop.
   */
  isHorizontal?: boolean
  /**
   * HTML tag to be used for the author name. Defaults to span.
   */
  authorNameTag?: ValidTypographyTags
} & HTMLProps<HTMLDivElement>
